import { SowInput, SowTextarea, SowSelect } from './ValidationFields';
import { Button } from '../Button';
import { PersonFields } from './PersonFields';
import { PhaseHoursEditor } from './PhaseHoursEditor';
import { fmt } from '../../lib/format';
import type { SowAccountContact, SowCoachingFields, SowCoachingPhase } from '../../types';

interface Props {
  fields: SowCoachingFields;
  contacts: SowAccountContact[];
  /** Standard phases per tier, from the prefill meta (absent in mock mode). */
  tierPresets?: Record<string, SowCoachingPhase[]>;
  /** Standard Desired Outcomes boilerplate, restored when picking a tier. */
  standardDesiredOutcomes?: string;
  onChange: (next: SowCoachingFields) => void;
}

const TIER_OPTIONS = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Coaching Extension'] as const;

export function CoachingSowForm({ fields, contacts, tierPresets, standardDesiredOutcomes, onChange }: Props): JSX.Element {
  const set = (patch: Partial<SowCoachingFields>) => onChange({ ...fields, ...patch });
  const feesPreview = `${fmt.cur(fields.feesTotal)} (${fmt.cur(fields.coachHourlyRate)}/Hour)`;

  const isKnownTier = (TIER_OPTIONS as readonly string[]).includes(fields.tierLabel);
  const isExtension = fields.tierLabel === 'Coaching Extension';

  function onTierSelect(value: string) {
    if (value === 'Other') {
      set({ tierLabel: '' });
      return;
    }
    if (value === 'Coaching Extension') {
      // Extension has no itemized Project Approach and no boilerplate Desired
      // Outcomes — both are copied from the parent SOW as free text.
      set({ tierLabel: value, phases: [], desiredOutcomes: '', projectApproach: '' });
      return;
    }
    const preset = tierPresets?.[value];
    set({
      tierLabel: value,
      projectApproach: '',
      ...(preset ? { phases: preset.map((p) => ({ ...p })) } : {}),
      ...(standardDesiredOutcomes ? { desiredOutcomes: standardDesiredOutcomes } : {}),
    });
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card-hd"><span className="d-h3">Engagement overview</span></div>
        <div className="card-bd" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label className="input-label">Tier</label>
            <SowSelect name={isKnownTier ? 'tierLabel' : undefined} className="select" value={isKnownTier ? fields.tierLabel : 'Other'} onChange={(e) => onTierSelect(e.target.value)}>
              {TIER_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
              <option value="Other">Other…</option>
            </SowSelect>
            {!isKnownTier && (
              <SowInput name="tierLabel"
                className="input"
                style={{ marginTop: 6 }}
                placeholder='Custom tier label, e.g. "Tier 2"'
                value={fields.tierLabel}
                onChange={(e) => set({ tierLabel: e.target.value })}
              />
            )}
            {isKnownTier && fields.tierLabel !== 'Coaching Extension' && (
              <span className="d-meta" style={{ fontSize: 11.5, color: 'var(--mute)' }}>Loads the standard {fields.tierLabel} phases below.</span>
            )}
          </div>
          <div>
            <label className="input-label">Client company</label>
            <SowInput name="clientCompany" className="input" value={fields.clientCompany} onChange={(e) => set({ clientCompany: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Ariel NetSuite Project #</label>
            <SowInput name="netsuiteProjectNumber" className="input d-mono" placeholder="P23526" value={fields.netsuiteProjectNumber} onChange={(e) => set({ netsuiteProjectNumber: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Engagement location</label>
            <SowInput name="engagementLocation" className="input" value={fields.engagementLocation} onChange={(e) => set({ engagementLocation: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Contracted hours</label>
            <SowInput name="contractedHours"
              className="input d-tnum"
              type="number"
              min={0}
              step={0.5}
              value={Number.isFinite(fields.contractedHours) ? fields.contractedHours : ''}
              onChange={(e) => set({ contractedHours: e.target.value === '' ? 0 : Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="input-label">Timeframe</label>
            <SowInput name="timeframe" className="input" placeholder="July 1, 2026 – December 31, 2026" value={fields.timeframe} onChange={(e) => set({ timeframe: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">People</span></div>
        <div className="card-bd" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <PersonFields name="accountOwner" label="Ariel account owner" value={fields.accountOwner} onChange={(v) => set({ accountOwner: v })} showTitle={false} />
          <PersonFields name="csm" label="Ariel CSM" value={fields.csm} onChange={(v) => set({ csm: v })} showTitle={false} />
          <PersonFields name="coachee" label="Client (coachee)" value={fields.coachee} onChange={(v) => set({ coachee: v })} contacts={contacts} />
        </div>
      </div>

      <div className="card">
        <div className="card-hd">
          <span className="d-h3">Executive coach & fees</span>
          <span className="d-tnum" style={{ fontSize: 13, color: 'var(--mute)' }}>Document shows: <strong>{feesPreview}</strong></span>
        </div>
        <div className="card-bd" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <div>
            <label className="input-label">Executive coach</label>
            <SowInput name="coachName" className="input" value={fields.coachName} onChange={(e) => set({ coachName: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Coach hourly rate (USD)</label>
            <SowInput name="coachHourlyRate"
              className="input d-tnum"
              type="number"
              min={0}
              step={5}
              value={Number.isFinite(fields.coachHourlyRate) ? fields.coachHourlyRate : ''}
              onChange={(e) => set({ coachHourlyRate: e.target.value === '' ? 0 : Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="input-label">Fees to coach — total (USD)</label>
            <SowInput name="feesTotal"
              className="input d-tnum"
              type="number"
              min={0}
              step={5}
              value={Number.isFinite(fields.feesTotal) ? fields.feesTotal : ''}
              onChange={(e) => set({ feesTotal: e.target.value === '' ? 0 : Number(e.target.value) })}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => set({ feesTotal: Math.round(fields.contractedHours * fields.coachHourlyRate * 100) / 100 })}
            title="Set total = contracted hours × rate"
          >
            Recalculate
          </Button>
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">Desired outcomes</span></div>
        <div className="card-bd">
          <SowTextarea name="desiredOutcomes"
            className="input"
            rows={5}
            style={{ width: '100%', height: 'auto', minHeight: 108, padding: '8px 10px', resize: 'vertical', lineHeight: 1.5 }}
            placeholder={isExtension ? 'Paste the Desired Outcomes section copied from the parent SOW…' : 'The desired outcomes will include: …'}
            value={fields.desiredOutcomes}
            onChange={(e) => set({ desiredOutcomes: e.target.value })}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">Project approach</span></div>
        <div className="card-bd">
          {isExtension ? (
            <SowTextarea name="projectApproach"
              className="input"
              rows={6}
              style={{ width: '100%', height: 'auto', minHeight: 132, padding: '8px 10px', resize: 'vertical', lineHeight: 1.5 }}
              placeholder="Paste the Project Approach section copied from the parent SOW…"
              value={fields.projectApproach}
              onChange={(e) => set({ projectApproach: e.target.value })}
            />
          ) : (
            <PhaseHoursEditor
              phases={fields.phases}
              contractedHours={fields.contractedHours}
              onChange={(phases) => set({ phases })}
            />
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">Special instructions for the coach</span></div>
        <div className="card-bd">
          <SowTextarea name="specialInstructions"
            className="input"
            rows={3}
            style={{ width: '100%', height: 'auto', minHeight: 72, padding: '8px 10px', resize: 'vertical', lineHeight: 1.5 }}
            placeholder='e.g. "No assessment or interviews requested at time of contracting. Please conduct an intake meeting…"'
            value={fields.specialInstructions}
            onChange={(e) => set({ specialInstructions: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}
